/**
 * Influencer Video Testimonials Section JavaScript
 * Handles video playback, testimonial switching, and UI interactions
 */

function influencerSection(optionsOrTotal) {
  const opts =
    optionsOrTotal && typeof optionsOrTotal === "object"
      ? optionsOrTotal
      : { total: Number(optionsOrTotal) || 0 };
  const totalTestimonials = Number(opts.total) || 0;
  const autoplayOnView = Boolean(opts.autoplayOnView);
  const loopToNext = Boolean(opts.loopToNext);
  const unmutedByDefault =
    opts.unmutedByDefault === undefined ? true : Boolean(opts.unmutedByDefault);
  const mobileTextAlign = opts.mobileTextAlign || "left";
  return {
    // State management
    activeTestimonial: 1,
    selectedTag: "all",
    isPlaying: {},
    isMuted: {},
    progress: {},
    durations: {},
    autoplayOnView,
    loopToNext,
    unmutedByDefault,
    mobileTextAlign,

    // Initialize the component
    init() {
      console.log(
        "Influencer section initialized with",
        totalTestimonials,
        "testimonials"
      );

      // Initialize state for all testimonials
      for (let i = 1; i <= totalTestimonials; i++) {
        this.isPlaying[i] = false;
        this.isMuted[i] = true; // Start muted for better UX
        this.progress[i] = 0;
        this.durations[i] = 0;
      }

      // Set up video event listeners
      this.setupVideoEventListeners();

      // Ensure default mute state
      this.$nextTick(() => {
        for (let i = 1; i <= totalTestimonials; i++) {
          const videoEl = this.$refs[`video${i}`];
          if (videoEl) {
            videoEl.muted = !this.unmutedByDefault ? true : false;
            this.isMuted[i] = videoEl.muted;
          }
        }
      });

      // Autoplay behavior
      if (totalTestimonials > 0) {
        if (this.autoplayOnView && this.$el) {
          this.setupAutoplayObserver();
        } else {
          this.$nextTick(() => {
            setTimeout(() => {
              this.playVideo(1);
            }, 500);
          });
        }
      }
    },

    // Set up event listeners for all videos
    setupVideoEventListeners() {
      this.$nextTick(() => {
        for (let i = 1; i <= totalTestimonials; i++) {
          const video = this.$refs[`video${i}`];
          if (video) {
            video.addEventListener("loadedmetadata", () => {
              this.updateDuration(i, video.duration);
            });

            video.addEventListener("timeupdate", () => {
              this.updateProgress(i, video.currentTime);
            });

            video.addEventListener("ended", () => {
              this.onVideoEnded(i);
            });
          }
        }
      });
    },

    // Setup IntersectionObserver for autoplay on view
    setupAutoplayObserver() {
      try {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.playVideo(this.activeTestimonial || 1);
                observer.disconnect();
              }
            });
          },
          { threshold: 0.25 }
        );
        observer.observe(this.$el);
      } catch (e) {
        // Fallback: attempt to play after delay
        setTimeout(() => this.playVideo(this.activeTestimonial || 1), 600);
      }
    },

    // Switch to a different testimonial
    switchTestimonial(index) {
     // console.log("Switching to testimonial", index);

      // Pause current video
      if (this.activeTestimonial && this.activeTestimonial !== index) {
        const currentVideo = this.$refs[`video${this.activeTestimonial}`];
        if (currentVideo) {
          currentVideo.pause();
          this.isPlaying[this.activeTestimonial] = false;
        }
      }

      // Update active testimonial
      this.activeTestimonial = index;

      // Auto-play new video after a short delay
      this.$nextTick(() => {
        setTimeout(() => {
          this.playVideo(index);
        }, 100);
      });
    },

    // Toggle play/pause for a specific video
    togglePlay(index) {
      const video = this.$refs[`video${index}`];
      if (!video) return;

      if (this.isPlaying[index]) {
        video.pause();
        this.isPlaying[index] = false;
      } else {
        video
          .play()
          .then(() => {
            this.isPlaying[index] = true;
          })
          .catch((e) => {
            console.log("Play failed:", e);
          });
      }
    },

    // Play a specific video
    playVideo(index) {
      const video = this.$refs[`video${index}`];
      if (video) {
        video
          .play()
          .then(() => {
            this.isPlaying[index] = true;
          })
          .catch((e) => {
            console.log("Auto-play prevented:", e);
            // Auto-play was prevented, which is normal
          });
      }
    },

    // Pause a specific video
    pauseVideo(index) {
      const video = this.$refs[`video${index}`];
      if (video) {
        video.pause();
        this.isPlaying[index] = false;
      }
    },

    // Toggle mute/unmute for a specific video
    toggleMute(index) {
      const video = this.$refs[`video${index}`];
      if (!video) return;

      video.muted = !video.muted;
      this.isMuted[index] = video.muted;
    },

    // Update video duration when metadata loads
    updateDuration(index, duration) {
      this.durations[index] = duration;
      console.log(`Video ${index} duration:`, duration);
    },

    // Update progress bar as video plays
    updateProgress(index, currentTime) {
      if (this.durations[index] > 0) {
        this.progress[index] = (currentTime / this.durations[index]) * 100;
      }
    },

    // Filter testimonials by tag (client-side UI state only)
    filterByTag(tag) {
      this.selectedTag = tag;
      //console.log("Filtering by tag:", tag);
      // Note: Actual filtering would require page reload or AJAX
      // This just updates the UI state for visual feedback
    },

    // Handle video ended event
    onVideoEnded(index) {
      this.isPlaying[index] = false;
      this.progress[index] = 0;

      if (this.loopToNext) {
        const nextIndex = index < totalTestimonials ? index + 1 : 1;
        setTimeout(() => {
          this.switchTestimonial(nextIndex);
        }, 500);
      }
    },

    // Handle video loading states
    onVideoLoadStart(index) {
      console.log(`Video ${index} loading started`);
    },

    onVideoCanPlay(index) {
      console.log(`Video ${index} can play`);
    },

    // Utility method to check if video is active
    isActiveVideo(index) {
      return this.activeTestimonial === index;
    },

    // Utility method to get video element
    getVideo(index) {
      return this.$refs[`video${index}`];
    },
  };
}

// Export for use in other contexts if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = { influencerSection };
}

// Fallback: enable basic MP4 playback without Alpine.js
// Ensures clicking play button or native controls works
document.addEventListener("DOMContentLoaded", function () {
  try {
    // If Alpine is present, the component handles interactions
    if (window.Alpine) return;

    var sectionRoots = document.querySelectorAll(".influencer-section");
    if (!sectionRoots.length) return;

    sectionRoots.forEach(function (root) {
      var autoplayOnView =
        String(root.getAttribute("data-autoplay-on-view")) === "true";
      var loopNext = String(root.getAttribute("data-loop-next")) === "true";
      var unmutedDefault =
        String(root.getAttribute("data-unmuted-default")) !== "false";

      // Ensure only the first video container is visible by default
      var containers = root.querySelectorAll(".video-area .video-container");
      containers.forEach(function (container, idx) {
        if (idx === 0) {
          if (container.hasAttribute("x-cloak")) {
            container.removeAttribute("x-cloak");
          }
          container.style.display = "";
        } else {
          container.style.display = "none";
        }
      });

      // Ensure only the first testimonial content is visible by default
      var contentBlocks = root.querySelectorAll(".testimonial-content > div");
      contentBlocks.forEach(function (block, idx) {
        if (idx === 0) {
          if (block.hasAttribute("x-cloak")) {
            block.removeAttribute("x-cloak");
          }
          block.style.display = "";
        } else {
          block.style.display = "none";
        }
      });

      // Wire up videos
      var videos = root.querySelectorAll(".video-area video");
      videos.forEach(function (video) {
        // Hide native controls; we'll use a single floating button
        video.controls = false;
        video.playsInline = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        // Respect unmuted default setting
        video.muted = !unmutedDefault;

        // Update play button icon state on play/pause
        var containerEl = video.closest(".video-container");
        var playBtn = containerEl
          ? containerEl.querySelector(".play-button")
          : null;
        // Avatar elements
        var avatarButtons = root.querySelectorAll(
          ".testimonial-avatars .testimonial-avatar, .influencer__avatars .influencer__avatar-btn"
        );
        function getAvatarProgressSvg() {
          var idx = Array.prototype.indexOf.call(containers, containerEl);
          var btn = avatarButtons[idx];
          return btn
            ? btn.querySelector(
                ".avatar-progress, .influencer__avatar-progress"
              )
            : null;
        }
        function setBtnState(isPlaying) {
          if (!playBtn) return;
          var svgs = playBtn.querySelectorAll("svg");
          if (svgs.length >= 2) {
            // First SVG is the play icon, second is pause icon
            svgs[0].style.display = isPlaying ? "none" : "";
            svgs[1].style.display = isPlaying ? "" : "none";
          }
        }
        function updateActiveAvatarClass() {
          var idx = Array.prototype.indexOf.call(containers, containerEl);
          var btn = avatarButtons[idx];
          if (!btn) return;
          // Clear all first
          avatarButtons.forEach(function (b) {
            b.classList.remove("active");
          });
          btn.classList.add("active");
        }

        video.addEventListener("play", function () {
          // Pause all other videos in this section
          videos.forEach(function (other) {
            if (other !== video) {
              other.pause();
            }
          });
          setBtnState(true);
          updateActiveAvatarClass();
        });
        video.addEventListener("pause", function () {
          setBtnState(false);
        });
        video.addEventListener("ended", function () {
          setBtnState(false);
          if (loopNext) {
            // find index of current container
            var idx = Array.prototype.indexOf.call(containers, containerEl);
            var nextIdx = idx + 1 < containers.length ? idx + 1 : 0;
            // switch visibility
            containers.forEach(function (c, i) {
              c.style.display = i === nextIdx ? "" : "none";
              if (i !== nextIdx) {
                var v = c.querySelector("video");
                if (v) v.pause();
              }
            });
            contentBlocks.forEach(function (block, i) {
              block.style.display = i === nextIdx ? "" : "none";
            });
            // autoplay next video
            var nextVideo =
              containers[nextIdx] && containers[nextIdx].querySelector("video");
            if (nextVideo) nextVideo.play().catch(function () {});
            // update active avatar class
            avatarButtons.forEach(function (b, i) {
              if (i === nextIdx) b.classList.add("active");
              else b.classList.remove("active");
            });
          }
        });

        // Update avatar circular progress in fallback
        video.addEventListener("timeupdate", function () {
          var ringEl = getAvatarProgressSvg();
          if (!ringEl || !isFinite(video.duration) || video.duration === 0)
            return;
          var pct = Math.max(
            0,
            Math.min(100, (video.currentTime / video.duration) * 100)
          );
          ringEl.style.setProperty("--progress", pct);
        });

        // Initialize button state
        setBtnState(false);
      });
      // Autoplay when section enters viewport
      if (autoplayOnView) {
        try {
          var observer = new IntersectionObserver(
            function (entries, obs) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  var firstVideo =
                    containers[0] && containers[0].querySelector("video");
                  if (firstVideo) firstVideo.play().catch(function () {});
                  obs.disconnect();
                }
              });
            },
            { threshold: 0.25 }
          );
          observer.observe(root);
        } catch (e) {
          var first = containers[0] && containers[0].querySelector("video");
          if (first)
            setTimeout(function () {
              first.play().catch(function () {});
            }, 400);
        }
      }

      // Wire up play buttons to toggle nearest video
      var playButtons = root.querySelectorAll(".video-area .play-button");
      playButtons.forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          var container = btn.closest(".video-container");
          if (!container) return;
          var video = container.querySelector("video");
          if (!video) return;
          if (video.paused) {
            var p = video.play();
            if (p && typeof p.then === "function") {
              p.catch(function () {
                // Autoplay might be blocked; keep controls hidden and wait for user action
              });
            }
          } else {
            video.pause();
          }
        });
      });

      // Also allow clicking directly on the video to toggle play/pause
      root.querySelectorAll(".video-area video").forEach(function (video) {
        video.addEventListener("click", function () {
          if (video.paused) {
            // Pause others first
            videos.forEach(function (other) {
              if (other !== video) {
                other.pause();
              }
            });
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        });
      });

      // Wire up avatar bubbles to switch testimonial (video + content)
      var avatarButtons = root.querySelectorAll(
        ".testimonial-avatars .testimonial-avatar"
      );
      function switchToIndex(idx) {
        // Hide all
        containers.forEach(function (c, i) {
          c.style.display = i === idx ? "" : "none";
          if (i !== idx) {
            var v = c.querySelector("video");
            if (v) {
              v.pause();
            }
          }
        });
        contentBlocks.forEach(function (block, i) {
          block.style.display = i === idx ? "" : "none";
        });
        // Update avatar active state
        avatarButtons.forEach(function (b, i) {
          if (i === idx) {
            b.classList.add("active");
          } else {
            b.classList.remove("active");
          }
        });
        // Auto-play the newly active video
        var activeVideo =
          containers[idx] && containers[idx].querySelector("video");
        if (activeVideo) {
          activeVideo.play().catch(function () {});
        }
      }

      avatarButtons.forEach(function (btn, idx) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          switchToIndex(idx);
        });
      });
    });
  } catch (err) {
    // Fail silently; do not break the storefront
    console &&
      console.warn &&
      console.warn("Influencer fallback init failed", err);
  }
});
